import { Button, Checkbox, LoadingSpinner } from "@/components/ui";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import { cn } from "@/lib/utils/cn";
import { type MentionOption } from "@/lib/utils/mentionUtils";
import { EditorContent } from "@tiptap/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

type ContactNoteComposerProps = {
  mentionOptions: MentionOption[];
  isMentionLoading?: boolean;
  initialContent?: string;
  initialDraft?: boolean;
  showDraftToggle?: boolean;
  placeholder?: string;
  primaryButtonLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  onSubmit: (message: string, isDraft: boolean) => Promise<void> | void;
  onCancel?: () => void;
  onChange?: (message: string, isDraft: boolean) => void;
  onAutoSaveDraft?: (message: string) => void;
  autoSaveOnUnmount?: boolean;
  className?: string;
  maxLength?: number;
  showPrimaryButton?: boolean;
};

export const ContactNoteComposer = ({
  mentionOptions,
  isMentionLoading = false,
  initialContent = "",
  initialDraft = false,
  showDraftToggle = true,
  placeholder = "Add a note...",
  primaryButtonLabel = "Post note",
  cancelLabel = "Cancel",
  submitting = false,
  onSubmit,
  onCancel,
  onChange,
  onAutoSaveDraft,
  autoSaveOnUnmount = false,
  className,
  maxLength = 5000,
  showPrimaryButton = true,
}: ContactNoteComposerProps) => {
  const [noteDraft, setNoteDraft] = useState(initialContent);
  const [isDraft, setIsDraft] = useState(initialDraft);
  const draftSavedRef = useRef(false);
  const draftCheckboxId = useId();

  const { editor, getBackendMessage, setContent, mentionDropdownOpenRef } =
    useTiptapMentions({
      mentionOptions,
      isLoading: isMentionLoading,
      onUpdate: (value) => {
        setNoteDraft(value);
      },
      initialContent,
      isEditable: true,
      className:
        "max-h-[240px] min-h-[120px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
      placeholder,
    });

  const lastAppliedInitialRef = useRef<string | null>(null);
  const autoSaveContextRef = useRef<{
    noteDraft: string;
    getBackendMessage: () => string;
    onAutoSaveDraft: ContactNoteComposerProps["onAutoSaveDraft"];
  }>({
    noteDraft: "",
    getBackendMessage: () => "",
    onAutoSaveDraft: undefined,
  });

  useEffect(() => {
    if (!editor) return;
    if (lastAppliedInitialRef.current === initialContent) {
      return;
    }
    lastAppliedInitialRef.current = initialContent;
    setNoteDraft(initialContent);
    setContent(initialContent);
  }, [editor, initialContent, setContent]);

  useEffect(() => {
    setIsDraft(initialDraft);
  }, [initialDraft]);

  useEffect(() => {
    if (noteDraft.trim()) {
      draftSavedRef.current = false;
    }
  }, [noteDraft]);

  useEffect(() => {
    autoSaveContextRef.current = {
      noteDraft,
      getBackendMessage,
      onAutoSaveDraft,
    };
  }, [noteDraft, getBackendMessage, onAutoSaveDraft]);

  useEffect(() => {
    onChange?.(getBackendMessage(), isDraft);
  }, [noteDraft, isDraft, getBackendMessage, onChange]);

  useEffect(() => {
    if (!autoSaveOnUnmount) {
      return;
    }

    return () => {
      const context = autoSaveContextRef.current;
      if (draftSavedRef.current || !context.noteDraft.trim()) {
        return;
      }

      const draftMessage = context.getBackendMessage();
      if (maxLength > 0 && draftMessage.length > maxLength) {
        return;
      }

      draftSavedRef.current = true;
      context.onAutoSaveDraft?.(draftMessage);
    };
  }, [autoSaveOnUnmount, maxLength]);

  const canSubmit = useMemo(
    () => noteDraft.trim().length > 0 && !submitting,
    [noteDraft, submitting],
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const draftMessage = getBackendMessage();
    if (maxLength > 0 && draftMessage.length > maxLength) {
      toast.error("Message must be 5000 characters or less");
      return;
    }
    const previousDraft = noteDraft;
    const previousIsDraft = isDraft;

    try {
      await onSubmit(draftMessage, isDraft);
      draftSavedRef.current = true;
    } catch {
      setNoteDraft(previousDraft);
      setIsDraft(previousIsDraft);
      return;
    }

    if (!initialContent) {
      setContent("");
      setNoteDraft("");
      setIsDraft(false);
    }
  };

  return (
    <div
      className={cn(
        "border-border bg-card rounded-xl border shadow-sm",
        className,
      )}
    >
      <EditorContent
        editor={editor}
        onKeyDown={(event) => {
          if (
            event.key !== "Enter" ||
            event.shiftKey ||
            event.altKey ||
            event.metaKey ||
            event.ctrlKey
          ) {
            return;
          }

          if (mentionDropdownOpenRef.current) {
            return;
          }

          if (!showPrimaryButton || !canSubmit) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          void handleSubmit();
        }}
      />
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 px-4 py-2.5",
          showDraftToggle ? "justify-between" : "justify-end",
        )}
      >
        {showDraftToggle && (
          <label
            htmlFor={draftCheckboxId}
            className="text-muted-foreground flex items-center gap-2 text-sm font-medium"
          >
            <Checkbox
              id={draftCheckboxId}
              checked={isDraft}
              disabled={submitting}
              onCheckedChange={(checked) => {
                setIsDraft(checked === true);
              }}
            />
            Draft
          </label>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onCancel();
              }}
              disabled={submitting}
            >
              {cancelLabel}
            </Button>
          )}
          {showPrimaryButton && (
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={() => {
                void handleSubmit();
              }}
              className="flex items-center justify-center gap-2"
            >
              {submitting && (
                <LoadingSpinner className="h-4 w-4 text-current" />
              )}
              {primaryButtonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
